import { useState, useMemo } from "react";
import {
  DEFAULT_TABLE_FILTERS,
  FILTER_IDS_BLACKLIST,
  LIFECYCLE_FILTER_OPTIONS,
  PERCENT_COLUMNS_NOT_IN_DECIMAL_FORMAT,
} from "views/merchandise/tables/filters";
import { getTargetBidTypeFilter } from "views/advertising/targetLifecycleFilters";
import useTableSettings, { type CustomFilter } from "./useTableSettings";
import moment from "moment";

const useCustomTableFilters = (tableId: string) => {
  const { savedTableSettings: savedTableFilters, updateTableSettings: setSavedTableFilters } = useTableSettings(
    tableId,
    "tableFilters",
    [],
  );

  const filterSettings = useMemo(() => {
    const filters = savedTableFilters ?? DEFAULT_TABLE_FILTERS[tableId] ?? [];

    return filters
      .filter((filterOption) => {
        return !FILTER_IDS_BLACKLIST.includes(filterOption.id);
      })
      .map((filterOption) => {
        if (filterOption.override) {
          const matchedLifecycleOption = [...LIFECYCLE_FILTER_OPTIONS, getTargetBidTypeFilter(tableId)].find(
            (option) => option.id === filterOption.id,
          )!;

          filterOption.filterFunction = matchedLifecycleOption.filterFunction;
          return filterOption;
        }
        return filterOption;
      });
  }, [tableId, savedTableFilters]);

  const [customFilters, setCustomFilters] = useState(filterSettings);

  const compare = (
    rowValue: unknown,
    filterValue: unknown,
    operator: string,
    format = "number",
    options: Partial<CustomFilter> = {},
  ) => {
    // Toggle comparison options
    if (format === "toggle") {
      switch (operator) {
        case "on":
          return rowValue === options.onValue;
        case "off":
          return rowValue === options.offValue;
        case "all":
        default:
          return true;
      }
    }

    // Numeric comparison options
    const { min, max } = options;
    const minParsed = min ? parseFloat(min) : -Infinity;
    const maxParsed = max ? parseFloat(max) : Infinity;

    if (["currency", "percent", "number"].includes(format)) {
      const rowValueParsed = parseFloat(rowValue as string);
      const filterValueParsed = parseFloat(filterValue as string);

      switch (operator) {
        case "=":
          return rowValueParsed === filterValueParsed;
        case ">":
          return rowValueParsed > filterValueParsed;
        case "<":
          return rowValueParsed < filterValueParsed || !rowValueParsed;
        case "≥":
          return rowValueParsed >= filterValueParsed;
        case "≤":
          return rowValueParsed <= filterValueParsed || !rowValueParsed;
        // Below handles range thresholds
        case "-":
          return rowValueParsed >= minParsed && rowValueParsed <= maxParsed;
        default:
          return true;
      }
    }

    if (format === "date") {
      const rowValueParsed = moment(rowValue as string);
      const filterValueParsed = moment(filterValue as string);
      switch (operator) {
        case "-":
          return rowValueParsed.isSameOrAfter(min!, "d") && rowValueParsed.isSameOrBefore(max!, "d");
        case "=":
          return rowValueParsed.isSame(filterValueParsed, "d");
        case "<":
          return rowValueParsed.isBefore(filterValueParsed, "d");
        case ">=":
          return rowValueParsed.isSameOrAfter(filterValueParsed, "d");
        case ">":
          return rowValueParsed.isAfter(filterValueParsed, "d");
        case "<=":
          return rowValueParsed.isSameOrBefore(filterValueParsed, "d");
        default:
          return true;
      }
    }

    // Case-insensitive text comparison options (default/fallback)
    const rowValueParsed =
      typeof rowValue === "object" && rowValue ?
        Object.values(rowValue)
          .map((v) => v)
          .join("")
          .toLowerCase()
      : ((rowValue as string | undefined)?.toLowerCase() ?? "");
    const filterValueParsed = (filterValue as string | undefined)?.toLowerCase() ?? "";

    switch (operator) {
      case "=":
        return rowValueParsed === filterValueParsed;
      case "≠":
        return rowValueParsed !== filterValueParsed;
      case "^":
        return rowValueParsed.startsWith(filterValueParsed);
      case "$":
        return rowValueParsed.endsWith(filterValueParsed);
      case "∌":
        return !rowValueParsed.includes(filterValueParsed);
      case "⊃":
      default:
        return rowValueParsed.includes(filterValueParsed);
    }
  };

  const applyCustomFilters = (data?: Record<string, unknown>[]) => {
    // Purge incomplete filter options without needed values to match or missing operator
    const applicableFilters = customFilters.filter((filter) => {
      const isNumericFilter = filter.format && ["currency", "percent", "number"].includes(filter.format);
      return (
        filter.id &&
        filter.operator &&
        (filter.value ||
          (filter.min && filter.max) ||
          filter.format === "toggle" ||
          filter.override ||
          (isNumericFilter && !isNaN(parseFloat(filter.value as string))))
      );
    });

    if (!applicableFilters.length) {
      return data;
    }
    return data?.filter((row) => {
      for (let filterIndex = 0; filterIndex < applicableFilters.length; filterIndex++) {
        const {
          id,
          value,
          operator,
          format,
          min,
          max,
          onValue,
          offValue,
          override,
          filterFunction,
          overrideAccessorId,
        } = applicableFilters[filterIndex]!;

        const rowAccessor = overrideAccessorId ?? id;

        const sharedFiltersByColumnId = applicableFilters.filter((f) => f.id === applicableFilters[filterIndex]?.id);

        // Handle overrides - use custom filter function to return any rows that match criteria
        if (override) {
          // Multi-select values and support for multiple column filter criteria (not currently supported in markup)
          if (sharedFiltersByColumnId.length > 1 || typeof value === "object") {
            const valuesList = sharedFiltersByColumnId.map((f) => f.value);

            let match = false;
            const matchValues = valuesList.flat();

            if (operator === "allof") {
              match = matchValues.every((value) => filterFunction?.(row, value));
            } else {
              for (let matchValueIndex = 0; matchValueIndex < matchValues.length; matchValueIndex++) {
                if (filterFunction?.(row, matchValues[matchValueIndex])) {
                  match = true;
                }
              }
            }
            if (!match) {
              return false;
            }
          } else if (!filterFunction?.(row, value)) {
            return false;
          }
        } else if (PERCENT_COLUMNS_NOT_IN_DECIMAL_FORMAT.includes(id)) {
          const convertedRowValue =
            !isNaN(parseFloat(row[rowAccessor as string] as string)) ?
              parseFloat(row[rowAccessor as string] as string) / 100
            : null;
          return compare(convertedRowValue, value, operator, format, {
            min,
            max,
            onValue,
            offValue,
          });
        } else if (
          !compare(row[rowAccessor], value, operator, format, {
            min,
            max,
            onValue,
            offValue,
          })
        ) {
          return false;
        }
      }
      return true;
    });
  };

  const updateCustomFilters = (updatedFilters: CustomFilter[]) => {
    setCustomFilters(updatedFilters);

    const updatedTableFilters = updatedFilters.filter(
      (filterOption: { id: string }) => !FILTER_IDS_BLACKLIST.includes(filterOption.id),
    );

    setSavedTableFilters(updatedTableFilters);
  };

  return {
    customFilters,
    setCustomFilters: updateCustomFilters,
    applyCustomFilters,
  };
};

export default useCustomTableFilters;
